import { Bell, CheckCheck, FileText } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useNotificacoes,
  useMarcarNotificacaoLida,
  useMarcarTodasNotificacoesLidas,
} from "@/hooks/use-data";
import type { Notificacao } from "@shared/schema";

function formatRelativeTime(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return `há ${days}d`;
}

function NotificationItem({
  notificacao,
  onRead,
}: {
  notificacao: Notificacao;
  onRead: (id: string, laudoDbId?: string | null) => void;
}) {
  return (
    <button
      className={cn(
        "w-full text-left px-4 py-3 flex gap-3 hover:bg-white/5 transition-colors border-b border-white/10 last:border-0",
        !notificacao.lida && "bg-primary/10",
      )}
      onClick={() => onRead(notificacao.id, notificacao.laudoDbId)}
    >
      <div className="mt-0.5 shrink-0">
        <FileText
          className={cn(
            "w-4 h-4",
            notificacao.lida ? "text-white/30" : "text-primary",
          )}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm font-medium leading-snug",
            notificacao.lida ? "text-white/50" : "text-white",
          )}
        >
          {notificacao.titulo}
        </p>
        <p className="text-xs text-white/40 mt-0.5 leading-snug">{notificacao.mensagem}</p>
        <p className="text-xs text-white/30 mt-1">{formatRelativeTime(notificacao.criadaEm)}</p>
      </div>
      {!notificacao.lida && (
        <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
      )}
    </button>
  );
}

export function NotificationBell() {
  const { data: notificacoes = [] } = useNotificacoes();
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodas = useMarcarTodasNotificacoesLidas();
  const [, setLocation] = useLocation();

  const naoLidas = notificacoes.filter((n) => !n.lida).length;

  function handleRead(id: string, laudoDbId?: string | null) {
    marcarLida.mutate(id);
    if (laudoDbId) {
      setLocation(`/laudos/${laudoDbId}`);
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors">
          <Bell className="w-5 h-5" />
          {naoLidas > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
              {naoLidas > 99 ? "99+" : naoLidas}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={8}
        className="w-80 p-0 bg-slate-800 border-white/10 text-white shadow-xl"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="text-sm font-semibold">Notificações</h3>
          {naoLidas > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-white/50 hover:text-white hover:bg-white/10 gap-1"
              onClick={() => marcarTodas.mutate()}
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Marcar todas
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notificacoes.length === 0 ? (
            <div className="px-4 py-8 text-center text-white/30 text-sm">
              Nenhuma notificação
            </div>
          ) : (
            notificacoes.map((n) => (
              <NotificationItem key={n.id} notificacao={n} onRead={handleRead} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
